import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './StackNavigator.scss';
import TransitionPage from './components/TransitionPage';
import transitions from './transitions.scss';

const convertItemsToPages = (navItems, path = '/', pagesObj = {}) => {
  let modifiedPagesObj = {
    ...pagesObj,
    [path]: { items: navItems, path },
  };
  navItems.filter(navItem => navItem.navigationItems?.length).forEach((navItem) => {
    const itemName = navItem.text || navItem.page?.title;
    const newNavName = `${path}${path === '/' ? '' : '/'}${itemName}`;
    const deeperObj = convertItemsToPages(navItem.navigationItems, newNavName, modifiedPagesObj);
    modifiedPagesObj = {
      ...modifiedPagesObj,
      ...deeperObj,
    };
  });
  return modifiedPagesObj;
};

const StackNavigator = ({
  transition = 'none',
  items,
  visible,
  back = undefined,
  updateTitle,
  primaryButton,
  secondaryButton,
  isDesktop = false,
}) => {
  // TODO: Possible future reducer here
  const [pages, updatePages] = useState({});
  const [navStack, updateNavStack] = useState([]);
  const [stackTitle, updateStackTitle] = useState('');
  const [stackBack, updateStackBack] = useState(false);
  const [direction, updateDirection] = useState(null);

  const updateComponentTitle = updateTitle || updateStackTitle;

  // Prevents the flash on first load for the StackNavigator
  const [firstLoad, updateFirstLoad] = useState(true);
  useEffect(() => {
    if (visible && firstLoad) {
      updateFirstLoad(false);
    }
  }, [visible]);

  const toggleBack = () => updateStackBack(!stackBack);

  const createPages = () => {
    const convertedPages = convertItemsToPages(items);
    updatePages(convertedPages);
  };

  useEffect(createPages, [items]);

  const convertPathToTitle = (path) => {
    const splitPaths = path.split('/');
    return splitPaths.slice(-1)[0];
  };

  const addToStack = (path) => {
    const modifiedNavStack = navStack.map(navItem => ({
      ...navItem,
      visible: false,
    }));
    modifiedNavStack.push({ path, visible: true });
    updateDirection('forwards');
    updateNavStack(modifiedNavStack);
    updateComponentTitle(convertPathToTitle(path));
  };

  const hideAll = () => {
    const modifiedNavStack = navStack.map(navItem => ({ ...navItem, visible: false }));
    updateNavStack(modifiedNavStack);
    updateDirection(null);
  };

  useEffect(() => {
    if (firstLoad) { return; }

    const modifiedNavStack = navStack.map((navItem, index) => ({
      ...navItem,
      visible: index === navStack.length - 2,
    }));
    const lastElement = modifiedNavStack.pop();
    lastElement.visible = false;
    modifiedNavStack.push(lastElement);

    updateDirection('back');
    updateNavStack(modifiedNavStack);
    updateComponentTitle(convertPathToTitle(modifiedNavStack.slice(-2)[0].path));
  }, [back, stackBack]);

  useEffect(() => {
    if (visible) {
      addToStack('/');
    } else {
      hideAll();
    }
  }, [visible]);

  useEffect(() => {
    const lastItem = navStack.slice(-1)[0];
    if (!lastItem) { return; }

    let isAnyVisible = false;
    navStack.forEach((navItem) => {
      if (navItem.visible) {
        isAnyVisible = true;
      }
    });

    if (!isAnyVisible && isDesktop) {
      updateComponentTitle('');
      updateNavStack([]);
      return;
    }
    if (!isAnyVisible && !isDesktop) {
      setTimeout(() => {
        updateComponentTitle('');
        updateNavStack([]);
      }, 300);
      return;
    }

    if (!lastItem.visible) {
      setTimeout(() => {
        const modifiedNavStack = [...navStack];
        modifiedNavStack.pop();
        updateNavStack(modifiedNavStack);
      }, 300);
    }
  }, [navStack]);

  return (
    <div className={visible ? cx(styles.wrapper) : ''}>
      {navStack.map(({ path, visible: isVisible }, index) => {
        const item = pages[path];
        return (
          <TransitionPage
            key={item.path}
            page={item}
            visible={isVisible}
            {...{ transition,
              addToStack,
              isDesktop,
              stackTitle,
              toggleBack,
              index,
              direction }}
          />
        );
      })}
     {(primaryButton || secondaryButton) && visible ? (
        <div
          className={cx(styles.buttonWrapper,
            firstLoad && styles.hiddenPage,
            !visible && transitions[`${transition}TransitionOut`],
            visible && transitions[`${transition}TransitionIn`])}
        >
          {secondaryButton && (
            <a
              className={cx(styles.button, styles.secondaryButton, 'color-primary-border', 'color-primary-foreground')}
              href={secondaryButton.link?.href || secondaryButton.page?.path}
              target={secondaryButton.link?.target || '_self'}
            >
              {secondaryButton.text || secondaryButton.page?.title}
            </a>
          )}
          {primaryButton && (
            <a
              className={cx(styles.button, styles.primaryButton, 'color-primary-background', 'color-primary-border')}
              href={primaryButton.link?.href || primaryButton.page?.path}
              target={primaryButton.link?.target || '_self'}
            >
              {primaryButton.text || primaryButton.page?.title}
            </a>
          )}
        </div>
     ) : null}
    </div>
  );
};

export default StackNavigator;
